package com.tovtalk.data.repository

import com.tovtalk.data.mapper.UserMapper
import com.tovtalk.data.remote.ApiClient
import com.tovtalk.data.remote.ApiEndpoints
import com.tovtalk.data.remote.dto.DeviceTokenResponse
import com.tovtalk.data.remote.dto.FirebaseCustomTokenResponse
import com.tovtalk.data.remote.dto.DeleteAccountResponse
import com.tovtalk.data.remote.dto.LoginResponse
import com.tovtalk.data.remote.dto.UserDto
import com.tovtalk.data.remote.dto.V2PaginatedData
import com.tovtalk.data.remote.dto.FollowPreferencesDto
import com.tovtalk.data.remote.dto.V2Response
import com.tovtalk.data.remote.dto.ApiErrorBody
import com.tovtalk.data.remote.dto.ReportUserRequest
import com.tovtalk.data.remote.dto.SessionDto
import com.tovtalk.data.remote.dto.SessionsWrapper
import com.tovtalk.data.remote.dto.VerificationStartDto
import com.tovtalk.data.remote.dto.VerificationStatusDto
import com.tovtalk.domain.entity.ERROR_CODE_KASHRUT_NOT_ALLOWED
import com.tovtalk.domain.entity.*
import com.tovtalk.domain.entity.SessionInfo
import com.tovtalk.domain.repository.UserRepository
import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.request.forms.*
import io.ktor.client.statement.bodyAsText
import io.ktor.http.*
import com.tovtalk.data.cache.LocalCacheManager
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import com.tovtalk.data.local.TokenManager
import com.tovtalk.data.local.clearAllAppDataForAccountDeletion
import com.tovtalk.data.remote.dto.V2UserData
import io.ktor.http.content.TextContent
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.JsonPrimitive

/**
 * User Repository Implementation
 */
@OptIn(ExperimentalTime::class)
class UserRepositoryImpl(
    private val apiClient: ApiClient,
    private val authManager: com.tovtalk.domain.repository.AuthManager,
    private val cacheManager: LocalCacheManager
) : UserRepository {
    
    private companion object {
        // Mirrors the TTL pattern in AppRepositoryImpl.
        const val DETAILS_TTL_MS = 300_000L // 5 minutes
        const val DETAILS_CACHE_MAX = 100 // bound the map to avoid unbounded growth
    }

    private data class CachedUser(
        val user: User,
        val timestamp: Long
    )

    /** Stores a user in detailsCache with a TTL timestamp and a simple size-cap LRU eviction. */
    private fun putDetailsCache(userId: Int, user: User) {
        if (detailsCache.size >= DETAILS_CACHE_MAX && !detailsCache.containsKey(userId)) {
            // Evict the oldest entry by timestamp.
            detailsCache.minByOrNull { it.value.timestamp }?.key?.let { detailsCache.remove(it) }
        }
        detailsCache[userId] = CachedUser(user, Clock.System.now().toEpochMilliseconds())
    }

    /**
     * Drops any cached copy of [userId] so the next read re-fetches fresh state. Called
     * after follow/unfollow/block/unblock — otherwise the stale [detailsCache] entry
     * (5-min TTL) keeps showing the old isFollowing flag / follower counts when the user
     * navigates away and back, since ProfileViewModel is a factory and loses its optimistic
     * patch on recreation. Also clears [cachedUserFlow] when it points at the same user.
     */
    private fun invalidateUserCache(userId: Int) {
        detailsCache.remove(userId)
        if (cachedUserFlow.value?.id == userId) {
            cachedUserFlow.value = null
        }
    }

    private val lenientJson = Json { ignoreUnknownKeys = true; isLenient = true }
    private val cachedUserFlow = MutableStateFlow<User?>(null)
    private val detailsCache = mutableMapOf<Int, CachedUser>()
    private val usernameToId = mutableMapOf<String, Int>()
    // Server uses last_id (cursor) pagination for followers; the public API exposes
    // a page index. Track the server-provided next_cursor per user: page <= 0 starts
    // fresh, later pages send the stored cursor.
    private val followersCursors = mutableMapOf<Int, String?>()
    /** Prevents multiple concurrent network calls for getCurrentUser when cache is cold. */
    private val currentUserMutex = kotlinx.coroutines.sync.Mutex()
    
    override suspend fun login(
        identity: String,
        password: String?,
        loginMethod: LoginMethod,
        isHadran: Boolean,
        kosherLevelId: Int?
    ): DomainResult<User> = try {
        val endpoint = when (loginMethod) {
            LoginMethod.GOOGLE -> ApiEndpoints.LOGIN_FIREBASE
            else -> ApiEndpoints.LOGIN
        }
        val jsonBody = buildJsonObject {
            when (loginMethod) {
                LoginMethod.GOOGLE -> {
                    put("firebase_token", JsonPrimitive(identity))
                    put("user_data", kotlinx.serialization.json.JsonNull)
                    put("is_hadran", JsonPrimitive(isHadran))
                }
                else -> {
                    put("username", JsonPrimitive(identity))
                    put("password", JsonPrimitive(password ?: ""))
                    put("is_hadran", JsonPrimitive(isHadran))
                }
            }
            if (kosherLevelId != null) put("kosher_level_id", JsonPrimitive(kosherLevelId))
            // Send guest_user_id for conversion (new persistent guests)
            com.tovtalk.data.local.TokenManager.userId?.let {
                if (com.tovtalk.data.local.TokenManager.isGuest) {
                    put("guest_user_id", JsonPrimitive(it))
                }
            }
            com.tovtalk.data.local.TokenManager.deviceToken?.let {
                put("guest_device_token", JsonPrimitive(it))
            }
        }
        val jsonString = Json.encodeToString(JsonObject.serializer(), jsonBody)
        val response = apiClient.httpClient.post(endpoint) {
            setBody(TextContent(jsonString, ContentType.Application.Json))
        }

        if (response.status == HttpStatusCode.OK) {
            val loginResponse = response.body<LoginResponse>()
            if (loginResponse.status && loginResponse.data != null) {
                // Map user from login data
                val userDto = loginResponse.data.user
                
                if (userDto != null) {
                    val user = UserMapper.mapToEntity(userDto)
                    cacheUser(user)
                    
                    // Save Token
                    com.tovtalk.data.local.TokenManager.accessToken = loginResponse.data.accessToken
                    com.tovtalk.data.local.TokenManager.refreshToken = loginResponse.data.refreshToken
                    com.tovtalk.data.local.TokenManager.isGuest = false
                    apiClient.clearBearerTokenCache()

                    // Sign in to Firebase for chat (email users get custom token from server)
                    val customToken = loginResponse.data.firebaseCustomToken
                    if (customToken != null && customToken.isNotBlank()) {
                        com.tovtalk.utils.logger.Logger.d("Firebase custom token received (length=${customToken.length}), calling signInWithCustomToken…", "UserRepositoryImpl")
                        when (val r = authManager.signInWithCustomToken(customToken)) {
                            is DomainResult.Success -> com.tovtalk.utils.logger.Logger.d("Firebase sign-in for chat: OK", "UserRepositoryImpl")
                            is DomainResult.Error -> com.tovtalk.utils.logger.Logger.w("Firebase sign-in for chat failed: ${r.message}", "UserRepositoryImpl")
                            else -> {}
                        }
                    } else {
                        com.tovtalk.utils.logger.Logger.w("No firebase_custom_token in login response – Firestore/chat may fail (missing or insufficient permissions)", "UserRepositoryImpl")
                    }
                    
                    DomainResult.Success(user)
                } else {
                    DomainResult.Error("Login data missing user information")
                }
            } else {
                DomainResult.Error(loginResponse.message ?: "Login failed")
            }
        } else {
            val errorBody = response.bodyAsText()
            DomainResult.Error("Server error: ${response.status.value} - $errorBody")
        }
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("login failed: ${e.message}", "UserRepositoryImpl", e)
        e.printStackTrace()
        DomainResult.Error("Network error: ${e.message}", e)
    }
    
    override suspend fun register(
        fullname: String,
        email: String,
        password: String,
        loginMethod: LoginMethod
    ): DomainResult<User> = try {
        val jsonBody = buildJsonObject {
            put("fullname", JsonPrimitive(fullname))
            put("email", JsonPrimitive(email))
            put("password", JsonPrimitive(password))
            put("login_method", JsonPrimitive(loginMethod.name.lowercase()))
            // Send guest_user_id for conversion (new persistent guests)
            com.tovtalk.data.local.TokenManager.userId?.let {
                if (com.tovtalk.data.local.TokenManager.isGuest) {
                    put("guest_user_id", JsonPrimitive(it))
                }
            }
            com.tovtalk.data.local.TokenManager.deviceToken?.let {
                put("guest_device_token", JsonPrimitive(it))
            }
        }
        val jsonString = Json.encodeToString(JsonObject.serializer(), jsonBody)
        val httpResponse = apiClient.httpClient.post(ApiEndpoints.REGISTER) {
            setBody(TextContent(jsonString, ContentType.Application.Json))
        }
        if (!httpResponse.status.isSuccess()) {
            val errorBody = httpResponse.bodyAsText()
            DomainResult.Error("Registration failed: ${httpResponse.status.description}. $errorBody")
        } else {
        val loginResponse = httpResponse.body<LoginResponse>()
        if (loginResponse.status && loginResponse.data != null) {
            val userDto = loginResponse.data.user
            if (userDto != null) {
                val user = UserMapper.mapToEntity(userDto)
                cacheUser(user)
                com.tovtalk.data.local.TokenManager.accessToken = loginResponse.data.accessToken
                com.tovtalk.data.local.TokenManager.refreshToken = loginResponse.data.refreshToken
                com.tovtalk.data.local.TokenManager.isGuest = false
                apiClient.clearBearerTokenCache()
                // Sign in to Firebase for chat (email users get custom token from server)
                val customTokenReg = loginResponse.data.firebaseCustomToken
                if (customTokenReg != null && customTokenReg.isNotBlank()) {
                    com.tovtalk.utils.logger.Logger.d("Firebase custom token received (length=${customTokenReg.length}), calling signInWithCustomToken…", "UserRepositoryImpl")
                    when (val r = authManager.signInWithCustomToken(customTokenReg)) {
                        is DomainResult.Success -> com.tovtalk.utils.logger.Logger.d("Firebase sign-in for chat: OK", "UserRepositoryImpl")
                        is DomainResult.Error -> com.tovtalk.utils.logger.Logger.w("Firebase sign-in for chat failed: ${r.message}", "UserRepositoryImpl")
                        else -> {}
                    }
                } else {
                    com.tovtalk.utils.logger.Logger.w("No firebase_custom_token in register response – Firestore/chat may fail", "UserRepositoryImpl")
                }
                DomainResult.Success(user)
            } else {
                DomainResult.Error(loginResponse.message ?: "Registration failed")
            }
        } else {
            DomainResult.Error(loginResponse.message ?: "Registration failed")
        }
        }
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("register failed: ${e.message}", "UserRepositoryImpl", e)
        e.printStackTrace()
        DomainResult.Error("Network error: ${e.message}", e)
    }
    
    override suspend fun logout(): DomainResult<Unit> = try {
        apiClient.httpClient.post(ApiEndpoints.LOGOUT)
        com.tovtalk.data.local.TokenManager.clear()
        apiClient.clearBearerTokenCache()
        authManager.signOut()
        clearCache()
        DomainResult.Success(Unit)
    } catch (e: Exception) {
        // Even if API fails, we should clear local state
        com.tovtalk.data.local.TokenManager.clear()
        apiClient.clearBearerTokenCache()
        authManager.signOut()
        clearCache()
        com.tovtalk.utils.logger.Logger.e("logout failed: ${e.message}", "UserRepositoryImpl", e)
        e.printStackTrace()
        DomainResult.Error("Logout failed locally: ${e.message}", e)
    }

    override suspend fun continueAsGuest(kosherLevelId: Int?, consumptionCategoryIds: List<Int>?): DomainResult<Unit> = try {
        val jsonBody = buildJsonObject {
            kosherLevelId?.let { put("kosher_level_id", JsonPrimitive(it)) }
            consumptionCategoryIds?.takeIf { it.isNotEmpty() }?.let { ids ->
                put("consumption_category_ids", kotlinx.serialization.json.buildJsonArray { ids.forEach { add(JsonPrimitive(it)) } })
            }
            com.tovtalk.data.local.TokenManager.deviceToken?.let {
                put("device_id", JsonPrimitive(it))
            }
            put("is_hadran", JsonPrimitive(com.tovtalk.utils.config.isHadranDevice()))
        }
        val jsonString = Json.encodeToString(JsonObject.serializer(), jsonBody)
        val response = apiClient.httpClient.post(ApiEndpoints.GUEST_REGISTER) {
            setBody(TextContent(jsonString, ContentType.Application.Json))
        }
        if (response.status == HttpStatusCode.OK) {
            val loginResponse = response.body<LoginResponse>()
            if (loginResponse.status && loginResponse.data != null) {
                com.tovtalk.data.local.TokenManager.accessToken = loginResponse.data.accessToken
                com.tovtalk.data.local.TokenManager.refreshToken = loginResponse.data.refreshToken
                com.tovtalk.data.local.TokenManager.isGuest = true
                apiClient.clearBearerTokenCache()
                DomainResult.Success(Unit)
            } else {
                DomainResult.Error(loginResponse.message ?: "Guest registration failed")
            }
        } else {
            DomainResult.Error("Guest registration failed: ${response.status}")
        }
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("continueAsGuest failed: ${e.message}", "UserRepositoryImpl", e)
        DomainResult.Error("Network error: ${e.message}", e)
    }
    
    override suspend fun deleteAccount(): DomainResult<Unit> {
        return try {
            val httpResponse = apiClient.httpClient.delete(ApiEndpoints.DELETE_ACCOUNT)
            if (!httpResponse.status.isSuccess()) {
                val errorBody = httpResponse.bodyAsText()
                DomainResult.Error("Server error: ${httpResponse.status.value} - $errorBody")
            } else {
                val response = httpResponse.body<DeleteAccountResponse>()
                if (response.status) {
                    clearCache()
                    TokenManager.clear()
                    apiClient.clearBearerTokenCache()
                    authManager.signOut()
                    clearAllAppDataForAccountDeletion()
                    DomainResult.Success(Unit)
                } else {
                    DomainResult.Error(response.message ?: "Delete account failed")
                }
            }
        } catch (e: Exception) {
            com.tovtalk.utils.logger.Logger.e("deleteAccount failed: ${e.message}", "UserRepositoryImpl", e)
            e.printStackTrace()
            DomainResult.Error("Network error: ${e.message}", e)
        }
    }

    override suspend fun getUserDetails(userId: Int, forceRefresh: Boolean): DomainResult<User> {
        if (!forceRefresh) {
            detailsCache[userId]?.let { cached ->
                if (Clock.System.now().toEpochMilliseconds() - cached.timestamp < DETAILS_TTL_MS) {
                    return DomainResult.Success(cached.user)
                }
                detailsCache.remove(userId)
            }
        }
        return try {
            val httpResponse = apiClient.httpClient.get("${ApiEndpoints.USER_DETAILS}$userId")
            if (httpResponse.status == HttpStatusCode.Unauthorized) return DomainResult.Error("Unauthorized")
            val response = httpResponse.body<V2Response<V2UserData>>()
            response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
            val data = response.data?.user ?: return DomainResult.Error("Failed to get user details")
            val user = UserMapper.mapToEntity(data)
            putDetailsCache(userId, user)
            DomainResult.Success(user)
        } catch (e: Exception) {
            com.tovtalk.utils.logger.Logger.e("getUserDetails failed: ${e.message}", "UserRepositoryImpl", e)
            e.printStackTrace()
            DomainResult.Error("Network error: ${e.message}", e)
        }
    }

    override suspend fun getUserByUsername(username: String, forceRefresh: Boolean): DomainResult<User> {
        if (!forceRefresh) {
            usernameToId[username]?.let { userId ->
                detailsCache[userId]?.let { cached ->
                    if (Clock.System.now().toEpochMilliseconds() - cached.timestamp < DETAILS_TTL_MS) {
                        return DomainResult.Success(cached.user)
                    }
                    detailsCache.remove(userId)
                }
            }
        }
        return try {
            val response = apiClient.httpClient.get("${ApiEndpoints.USER_BY_USERNAME}$username")
                .body<V2Response<V2UserData>>()
            response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
            val data = response.data?.user ?: return DomainResult.Error("User not found")
            val user = UserMapper.mapToEntity(data)
            putDetailsCache(user.id, user)
            usernameToId[username] = user.id
            DomainResult.Success(user)
        } catch (e: Exception) {
            com.tovtalk.utils.logger.Logger.e("getUserByUsername failed: ${e.message}", "UserRepositoryImpl", e)
            DomainResult.Error("Network error: ${e.message}", e)
        }
    }
    
    override suspend fun getCurrentUser(forceRefresh: Boolean): DomainResult<User> {
        if (TokenManager.isGuestMode) return DomainResult.Error("Guest mode – no user")
        // Early return if there is no session at all. Hitting /users/me without a bearer token
        // returns 403 on the server and used to trigger clearAndNotifyExpired → hard reload loop.
        if (TokenManager.accessToken == null) return DomainResult.Error("No session")

        if (!forceRefresh) {
            // Return in-memory cached user if available to avoid unnecessary API calls
            cachedUserFlow.value?.let { user ->
                syncFirebaseAuthForChat()
                return DomainResult.Success(user)
            }

            // Check persistent LocalCacheManager (1-hour TTL) before hitting the network
            cacheManager.getUserProfile()?.let { profileJson ->
                try {
                    val userDto = Json.decodeFromString(UserDto.serializer(), profileJson)
                    val user = UserMapper.mapToEntity(userDto)
                    cacheUser(user)
                    syncFirebaseAuthForChat()
                    return DomainResult.Success(user)
                } catch (e: Exception) {
                    com.tovtalk.utils.logger.Logger.w("LocalCacheManager profile parse failed, fetching from network: ${e.message}", "UserRepositoryImpl")
                }
            }
        }

        // Coalesce concurrent network requests: if one is already in flight, await it
        return currentUserMutex.withLock {
            // Double-check cache inside the lock (another coroutine may have populated it)
            if (!forceRefresh) {
                cachedUserFlow.value?.let { user ->
                    syncFirebaseAuthForChat()
                    return@withLock DomainResult.Success(user)
                }
            }
            fetchCurrentUserFromNetwork()
        }
    }

    private suspend fun fetchCurrentUserFromNetwork(): DomainResult<User> = try {
        val httpResponse = apiClient.httpClient.get(ApiEndpoints.USER_ME)
        // Only 401 after Ktor's refresh already failed indicates true session expiry. 403 means
        // the *current* bearer is not authorized for /users/me (e.g. anonymous request landed here
        // despite the early-return guard, or guest token) — not a reason to wipe tokens and reload.
        if (httpResponse.status == HttpStatusCode.Unauthorized) {
            com.tovtalk.data.local.TokenManager.clearAndNotifyExpired()
            DomainResult.Error("Session expired")
        } else if (httpResponse.status == HttpStatusCode.Forbidden) {
            DomainResult.Error("Forbidden")
        } else {
            val rawBody = httpResponse.bodyAsText()
            val response = lenientJson.decodeFromString(V2Response.serializer(V2UserData.serializer()), rawBody)
            response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
            val data = response.data?.user ?: return DomainResult.Error("Failed to get current user")
            val user = UserMapper.mapToEntity(data)
            cacheUser(user)
            // Persist profile to local cache for subsequent cold starts
            try {
                cacheManager.saveUserProfile(Json.encodeToString(UserDto.serializer(), data))
            } catch (e: Exception) {
                com.tovtalk.utils.logger.Logger.w("Failed to persist user profile to LocalCacheManager: ${e.message}", "UserRepositoryImpl")
            }
            syncFirebaseAuthForChat()
            DomainResult.Success(user)
        }
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("getCurrentUser failed: ${e.message}", "UserRepositoryImpl", e)
        e.printStackTrace()
        DomainResult.Error("Network error: ${e.message}", e)
    }
    
    /**
     * When app has API session but Firebase is not signed in (e.g. cold start with stored tokens),
     * fetch a custom token and sign in so Firestore/chat works.
     */
    private suspend fun syncFirebaseAuthForChat() {
        if (authManager.getCurrentUserId() != null) return
        try {
            val response = apiClient.httpClient.get(ApiEndpoints.FIREBASE_CUSTOM_TOKEN)
                .body<FirebaseCustomTokenResponse>()
            val token = response.data?.firebaseCustomToken
            if (!token.isNullOrBlank()) {
                com.tovtalk.utils.logger.Logger.d("Firebase custom token (session restore) received, calling signInWithCustomToken…", "UserRepositoryImpl")
                when (val r = authManager.signInWithCustomToken(token)) {
                    is DomainResult.Success -> com.tovtalk.utils.logger.Logger.d("Firebase sign-in for chat (session restore): OK", "UserRepositoryImpl")
                    is DomainResult.Error -> com.tovtalk.utils.logger.Logger.w("Firebase sign-in for chat (session restore) failed: ${r.message}", "UserRepositoryImpl")
                    else -> {}
                }
            }
        } catch (e: Exception) {
            com.tovtalk.utils.logger.Logger.w("syncFirebaseAuthForChat failed: ${e.message}", "UserRepositoryImpl")
        }
    }
    
    override suspend fun updateUserProfile(
        fullname: String?,
        username: String?,
        bio: String?,
        profilePhoto: ByteArray?
    ): DomainResult<User> = try {
        val response = apiClient.httpClient.submitFormWithBinaryData(
            url = ApiEndpoints.UPDATE_PROFILE,
            formData = formData {
                fullname?.let { append("fullname", it) }
                username?.let { append("username", it) }
                bio?.let { append("bio", it) }
                profilePhoto?.let {
                    append("profile_photo", it, Headers.build {
                        append(HttpHeaders.ContentType, "image/jpeg")
                        append(HttpHeaders.ContentDisposition, "filename=profile.jpg")
                    })
                }
            }
        ).body<V2Response<V2UserData>>()

        response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        val data = response.data?.user ?: return DomainResult.Error("Update failed")
        val user = UserMapper.mapToEntity(data)
        cacheUser(user)
        DomainResult.Success(user)
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("updateUserProfile failed: ${e.message}", "UserRepositoryImpl", e)
        e.printStackTrace()
        DomainResult.Error("Network error: ${e.message}", e)
    }
    
    override suspend fun updateUserSettings(
        appLanguage: String?,
        whoCanViewPost: WhoCanViewPost?,
        showMyFollowing: Boolean?,
        receiveMessage: Boolean?,
        notifyPostLike: Boolean?,
        notifyPostComment: Boolean?,
        notifyFollow: Boolean?,
        notifyMention: Boolean?,
        notifyGiftReceived: Boolean?,
        notifyChat: Boolean?
    ): DomainResult<User> = try {
        val params = mutableMapOf<String, Any>()
        appLanguage?.let { params["app_language"] = it }
        whoCanViewPost?.let { params["who_can_view_post"] = it.value }
        showMyFollowing?.let { params["show_my_following"] = if (it) 1 else 0 }
        receiveMessage?.let { params["receive_message"] = if (it) 1 else 0 }
        notifyPostLike?.let { params["notify_post_like"] = if (it) 1 else 0 }
        notifyPostComment?.let { params["notify_post_comment"] = if (it) 1 else 0 }
        notifyFollow?.let { params["notify_follow"] = if (it) 1 else 0 }
        notifyMention?.let { params["notify_mention"] = if (it) 1 else 0 }
        notifyGiftReceived?.let { params["notify_gift_received"] = if (it) 1 else 0 }
        notifyChat?.let { params["notify_chat"] = if (it) 1 else 0 }
        
        val jsonBody = buildJsonObject {
            params.forEach { (k, v) ->
                put(k, when (v) {
                    is String -> JsonPrimitive(v)
                    is Boolean -> JsonPrimitive(v)
                    is Int -> JsonPrimitive(v)
                    is Long -> JsonPrimitive(v)
                    else -> JsonPrimitive(v.toString())
                })
            }
        }
        val jsonString = Json.encodeToString(JsonObject.serializer(), jsonBody)
        val httpResponse = apiClient.httpClient.patch(ApiEndpoints.UPDATE_SETTINGS) {
            setBody(TextContent(jsonString, ContentType.Application.Json))
        }
        if (!httpResponse.status.isSuccess()) {
            val errorBody = httpResponse.bodyAsText()
            val message = errorBody.takeIf { it.isNotBlank() } ?: "Settings update failed (${httpResponse.status})"
            DomainResult.Error(message)
        } else {
            val response = httpResponse.body<V2Response<V2UserData>>()
            response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
            val data = response.data?.user ?: return DomainResult.Error("Settings update failed")
            val user = UserMapper.mapToEntity(data)
            cacheUser(user)
            DomainResult.Success(user)
        }
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("updateUserSettings failed: ${e.message}", "UserRepositoryImpl", e)
        e.printStackTrace()
        DomainResult.Error("Network error: ${e.message}", e)
    }
    
    override suspend fun updateProfile(updates: Map<String, Any>): DomainResult<User> {
        return try {
            val jsonBody = buildJsonObject {
                updates.forEach { (k, v) ->
                    put(k, when (v) {
                        is String -> JsonPrimitive(v)
                        is Boolean -> JsonPrimitive(v)
                        is Int -> JsonPrimitive(v)
                        is Long -> JsonPrimitive(v)
                        is List<*> -> buildJsonArray {
                            (v as List<*>).forEach { e ->
                                add(when (e) {
                                    is Number -> JsonPrimitive(e.toLong())
                                    is Boolean -> JsonPrimitive(e)
                                    else -> JsonPrimitive(e.toString())
                                })
                            }
                        }
                        else -> JsonPrimitive(v.toString())
                    })
                }
            }
            val jsonString = Json.encodeToString(JsonObject.serializer(), jsonBody)
            val response = apiClient.httpClient.patch(ApiEndpoints.UPDATE_PROFILE) {
                setBody(TextContent(jsonString, ContentType.Application.Json))
            }
            if (!response.status.isSuccess()) {
                val errorBody = response.bodyAsText()
                DomainResult.Error("Update failed: ${response.status} - $errorBody")
            } else {
                val body = response.body<V2Response<V2UserData>>()
                body.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
                val data = body.data?.user ?: return DomainResult.Error("Update failed")
                val user = UserMapper.mapToEntity(data)
                cacheUser(user)
                DomainResult.Success(user)
            }
        } catch (e: Exception) {
            com.tovtalk.utils.logger.Logger.e("updateProfile failed: ${e.message}", "UserRepositoryImpl", e)
            e.printStackTrace()
            DomainResult.Error("Network error: ${e.message}", e)
        }
    }
    
    override suspend fun followUser(userId: Int): DomainResult<Unit> = try {
        val response = apiClient.httpClient.post("${ApiEndpoints.FOLLOW}$userId/follow")
        when (response.status) {
            HttpStatusCode.OK -> {
                val body = response.body<V2Response<Unit>>()
                body.errors?.takeIf { it.isNotEmpty() }?.let {
                    DomainResult.Error(it.first().message)
                } ?: run {
                    invalidateUserCache(userId)
                    DomainResult.Success(Unit)
                }
            }
            HttpStatusCode.Forbidden -> {
                val err = response.body<ApiErrorBody>()
                DomainResult.Error(
                    err.detail ?: "Forbidden",
                    null,
                    if (err.code == ERROR_CODE_KASHRUT_NOT_ALLOWED) err.code else null
                )
            }
            else -> DomainResult.Error("Follow failed: ${response.status}")
        }
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("followUser failed: ${e.message}", "UserRepositoryImpl", e)
        DomainResult.Error("Network error: ${e.message}", e)
    }
    
    override suspend fun unfollowUser(userId: Int): DomainResult<Unit> = try {
        val response = apiClient.httpClient.delete("${ApiEndpoints.UNFOLLOW}$userId/follow")
            .body<V2Response<Unit>>()

        response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        invalidateUserCache(userId)
        DomainResult.Success(Unit)
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("unfollowUser failed: ${e.message}", "UserRepositoryImpl", e)
        e.printStackTrace()
        DomainResult.Error("Network error: ${e.message}", e)
    }

    override suspend fun getFollowers(userId: Int, page: Int): DomainResult<List<User>> = try {
        // Server paginates by last_id cursor, not page index. page <= 0 starts fresh;
        // later pages send the server-provided next_cursor stored from the prior call.
        val cursor = if (page <= 0) null else followersCursors[userId]
        if (page > 0 && cursor == null && followersCursors.containsKey(userId)) {
            return DomainResult.Success(emptyList())
        }
        val response = apiClient.httpClient.get("${ApiEndpoints.USER_DETAILS}$userId/followers") {
            parameter("limit", 20)
            cursor?.let { parameter("cursor", it) }
        }.body<V2Response<V2PaginatedData<UserDto>>>()

        response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        val data = response.data
        followersCursors[userId] = data?.nextCursor
        DomainResult.Success(data?.items?.map { UserMapper.mapToEntity(it) } ?: emptyList())
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("getFollowers failed: ${e.message}", "UserRepositoryImpl", e)
        DomainResult.Error(e.message ?: "Failed to load followers")
    }

    override suspend fun getFollowing(userId: Int, page: Int): DomainResult<List<User>> = try {
        val response = apiClient.httpClient.get("${ApiEndpoints.USER_DETAILS}$userId/following") {
            parameter("limit", 20)
            parameter("page", page)
        }.body<V2Response<V2PaginatedData<UserDto>>>()

        response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        DomainResult.Success(response.data?.items?.map { UserMapper.mapToEntity(it) } ?: emptyList())
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("getFollowing failed: ${e.message}", "UserRepositoryImpl", e)
        DomainResult.Error(e.message ?: "Failed to load following")
    }

    override suspend fun getFollowPreferences(userId: Int): DomainResult<FollowPreferences> = try {
        val body = apiClient.httpClient.get("${ApiEndpoints.FOLLOW_PREFERENCES}$userId/follow-preferences")
            .body<V2Response<FollowPreferencesDto>>()

        body.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        val data = body.data ?: return DomainResult.Error("Failed to load follow preferences")
        DomainResult.Success(
            FollowPreferences(
                notifyNewPosts = data.notifyNewPosts,
                notifyNewStories = data.notifyNewStories,
                notifyLive = data.notifyLive
            )
        )
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("getFollowPreferences failed: ${e.message}", "UserRepositoryImpl", e)
        DomainResult.Error(e.message ?: "Failed to load follow preferences")
    }

    override suspend fun updateFollowPreferences(
        userId: Int,
        notifyNewPosts: Boolean?,
        notifyNewStories: Boolean?,
        notifyLive: Boolean?
    ): DomainResult<FollowPreferences> = try {
        val jsonBody = buildJsonObject {
            notifyNewPosts?.let { put("notify_new_posts", JsonPrimitive(it)) }
            notifyNewStories?.let { put("notify_new_stories", JsonPrimitive(it)) }
            notifyLive?.let { put("notify_live", JsonPrimitive(it)) }
        }
        val jsonString = Json.encodeToString(JsonObject.serializer(), jsonBody)
        val body = apiClient.httpClient.patch("${ApiEndpoints.FOLLOW_PREFERENCES}$userId/follow-preferences") {
            setBody(TextContent(jsonString, ContentType.Application.Json))
        }.body<V2Response<FollowPreferencesDto>>()

        body.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        val data = body.data ?: return DomainResult.Error("Failed to update follow preferences")
        DomainResult.Success(
            FollowPreferences(
                notifyNewPosts = data.notifyNewPosts,
                notifyNewStories = data.notifyNewStories,
                notifyLive = data.notifyLive
            )
        )
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("updateFollowPreferences failed: ${e.message}", "UserRepositoryImpl", e)
        DomainResult.Error(e.message ?: "Failed to update follow preferences")
    }

    override suspend fun reportUser(userId: Int, reasonId: Int, description: String): DomainResult<Unit> = try {
        val body = ReportUserRequest(
            userId = userId,
            reasonId = reasonId,
            details = description.takeIf { it.isNotBlank() }
        )
        val response = apiClient.httpClient.post(ApiEndpoints.REPORT_USER) {
            contentType(io.ktor.http.ContentType.Application.Json)
            setBody(body)
        }.body<V2Response<Unit>>()

        response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        DomainResult.Success(Unit)
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("reportUser failed: ${e.message}", "UserRepositoryImpl", e)
        DomainResult.Error(e.message ?: "Report failed")
    }

    override suspend fun blockUser(userId: Int): DomainResult<Unit> = try {
        val response = apiClient.httpClient.post("${ApiEndpoints.USER_DETAILS}$userId/block")
        when (response.status) {
            HttpStatusCode.OK -> {
                invalidateUserCache(userId)
                DomainResult.Success(Unit)
            }
            else -> DomainResult.Error("Failed to block: ${response.status}")
        }
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("blockUser failed: ${e.message}", "UserRepositoryImpl", e)
        DomainResult.Error(e.message ?: "Failed to block user")
    }

    override suspend fun unblockUser(userId: Int): DomainResult<Unit> = try {
        val response = apiClient.httpClient.delete("${ApiEndpoints.USER_DETAILS}$userId/block")
        when (response.status) {
            HttpStatusCode.OK -> {
                invalidateUserCache(userId)
                DomainResult.Success(Unit)
            }
            else -> DomainResult.Error("Failed to unblock: ${response.status}")
        }
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("unblockUser failed: ${e.message}", "UserRepositoryImpl", e)
        DomainResult.Error(e.message ?: "Failed to unblock user")
    }

    override suspend fun getBlockedUsers(limit: Int, lastId: Int?): DomainResult<List<User>> = try {
        val response = apiClient.httpClient.get(ApiEndpoints.BLOCKED_USERS) {
            parameter("limit", limit)
            lastId?.let { parameter("last_id", it) }
        }.body<V2Response<V2PaginatedData<UserDto>>>()

        response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        DomainResult.Success(response.data?.items?.map { UserMapper.mapToEntity(it) } ?: emptyList())
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("getBlockedUsers failed: ${e.message}", "UserRepositoryImpl", e)
        DomainResult.Error(e.message ?: "Failed to load blocked users")
    }

    override suspend fun searchUsers(query: String, page: Int): DomainResult<List<User>> {
        return try {
            val response = apiClient.httpClient.get(ApiEndpoints.SEARCH_USERS) {
                parameter("q", query)
                parameter("limit", 20)
                parameter("page", page)
            }.body<V2Response<V2PaginatedData<UserDto>>>()

            response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
            val users = response.data?.items?.map { UserMapper.mapToEntity(it) } ?: emptyList()
            DomainResult.Success(users)
        } catch (e: Exception) {
            com.tovtalk.utils.logger.Logger.e("searchUsers failed: ${e.message}", "UserRepositoryImpl", e)
            e.printStackTrace()
            DomainResult.Error(e.message ?: "Unknown search error")
        }
    }

    override suspend fun getSuggestedArtists(limit: Int, offset: Int): DomainResult<SuggestedArtistsResult> = try {
        // Server reads pagination from the `cursor` query param (offset as string),
        // not `offset`. Send the offset as the cursor so loadMore advances.
        val response = apiClient.httpClient.get(ApiEndpoints.SUGGESTED_USERS) {
            parameter("limit", limit)
            if (offset > 0) parameter("cursor", offset.toString())
        }.body<V2Response<V2PaginatedData<UserDto>>>()

        response.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        val data = response.data
        val list = data?.items?.map { UserMapper.mapToEntity(it) } ?: emptyList()
        DomainResult.Success(SuggestedArtistsResult(list = list, hasMore = data?.hasMore ?: false))
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("getSuggestedArtists failed: ${e.message}", "UserRepositoryImpl", e)
        DomainResult.Error(e.message ?: "Unknown error")
    }

    override suspend fun getSessions(): DomainResult<List<SessionInfo>> = try {
        val body = apiClient.httpClient.get(ApiEndpoints.SESSIONS)
            .body<V2Response<SessionsWrapper>>()

        body.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        DomainResult.Success(body.data?.sessions?.map {
            SessionInfo(it.id, it.isHadran, it.kosherLevelId, it.lastSeenAt, it.deviceId, it.userAgent, it.isCurrent)
        } ?: emptyList())
    } catch (e: Exception) {
        DomainResult.Error(e.message ?: "Network error")
    }

    override suspend fun deleteSession(sessionId: Int): DomainResult<Unit> = try {
        val response = apiClient.httpClient.delete("${ApiEndpoints.SESSIONS}/$sessionId")
        when (response.status) {
            HttpStatusCode.OK -> DomainResult.Success(Unit)
            HttpStatusCode.Forbidden -> {
                // Server has no error code here, only a detail string. Branch on the
                // typed detail (the "24 hours" rule) rather than scanning raw JSON.
                val detail = parseApiError(response)?.detail.orEmpty()
                val msg = if (detail.contains("24 hours") || detail.contains("24")) {
                    "לא ניתן להסיר מכשירים אחרים עד 24 שעות מהתחברות המכשיר הנוכחי"
                } else {
                    "אין הרשאה להסיר מכשיר זה"
                }
                DomainResult.Error(msg)
            }
            else -> DomainResult.Error("Failed to delete session")
        }
    } catch (e: Exception) {
        DomainResult.Error(e.message ?: "Network error")
    }

    override suspend fun updateKashrutLevel(kosherLevelId: Int): DomainResult<Unit> = try {
        val jsonBody = buildJsonObject {
            put("kosher_level_id", JsonPrimitive(kosherLevelId))
        }
        val jsonString = Json.encodeToString(JsonObject.serializer(), jsonBody)
        val response = apiClient.httpClient.patch("${ApiEndpoints.SESSIONS}/me") {
            setBody(TextContent(jsonString, ContentType.Application.Json))
        }
        when (response.status) {
            HttpStatusCode.OK -> DomainResult.Success(Unit)
            HttpStatusCode.Forbidden -> {
                // Server returns 403 with a detail string (no error code) when the
                // level requires gender verification. Branch on the typed detail.
                val detail = parseApiError(response)?.detail.orEmpty()
                if (detail.contains("verification required", ignoreCase = true)) {
                    DomainResult.Error("רמת כשרות זו דורשת אימות מגדר. נא להשלים תהליך אימות טלפוני.")
                } else {
                    DomainResult.Error("אין הרשאה לעדכן רמת כשרות זו")
                }
            }
            HttpStatusCode.BadRequest -> {
                val detail = parseApiError(response)?.detail
                if (detail?.contains("level 3 not allowed on Hadran") == true) {
                    DomainResult.Error("רמת כשרות זו אינה זמינה במכשיר הדרן")
                } else {
                    DomainResult.Error(detail ?: "שגיאה בעדכון רמת כשרות")
                }
            }
            else -> DomainResult.Error("Failed to update kosher level: ${response.status}")
        }
    } catch (e: Exception) {
        DomainResult.Error(e.message ?: "Network error")
    }

    override suspend fun updateDeviceToken(token: String, device: String): DomainResult<Unit> = try {
        val jsonBody = buildJsonObject {
            put("device_token", JsonPrimitive(token))
            put("device", JsonPrimitive(device))
        }
        val jsonString = Json.encodeToString(JsonObject.serializer(), jsonBody)
        val httpResponse = apiClient.httpClient.post(ApiEndpoints.DEVICE_TOKEN) {
            setBody(TextContent(jsonString, ContentType.Application.Json))
        }
        when (httpResponse.status) {
            HttpStatusCode.OK -> {
                val response = httpResponse.body<DeviceTokenResponse>()
                if (response.status) DomainResult.Success(Unit)
                else DomainResult.Error(response.message ?: "Failed to update device token")
            }
            HttpStatusCode.Forbidden, HttpStatusCode.Unauthorized -> {
                // User not logged in yet – skip without parsing error body (avoids JsonConvertException)
                DomainResult.Error("Not authenticated")
            }
            else -> DomainResult.Error("Server error: ${httpResponse.status}")
        }
    } catch (e: Exception) {
        com.tovtalk.utils.logger.Logger.e("updateDeviceToken failed: ${e.message}", "UserRepositoryImpl", e)
        e.printStackTrace()
        DomainResult.Error("Network error: ${e.message}", e)
    }

    override suspend fun startPhoneVerification(phone: String): DomainResult<com.tovtalk.domain.entity.VerificationRequestInfo> = try {
        val jsonBody = buildJsonObject {
            put("phone", JsonPrimitive(phone))
        }
        val jsonString = Json.encodeToString(JsonObject.serializer(), jsonBody)
        val response = apiClient.httpClient.post(ApiEndpoints.VERIFICATION_START) {
            setBody(TextContent(jsonString, ContentType.Application.Json))
        }
        if (response.status == HttpStatusCode.OK) {
            val body = response.body<V2Response<VerificationStartDto>>()
            body.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
            val d = body.data ?: return DomainResult.Error("Failed to start verification")
            DomainResult.Success(
                com.tovtalk.domain.entity.VerificationRequestInfo(
                    status = "pending",
                    code = d.code,
                    phoneToCall = d.phoneToCall
                )
            )
        } else if (response.status.value == 422) {
            // 422 is a Pydantic validation error whose body is a `detail` ARRAY,
            // not an {detail,code} object — so it can't be parsed as ApiErrorBody.
            // Substring matching on the raw body is the only viable path here.
            try {
                val errorBody = response.bodyAsText()
                if (errorBody.contains("String should have at least")) {
                    DomainResult.Error("מספר טלפון קצר מדי. נדרש לפחות 9 ספרות")
                } else if (errorBody.contains("already has an active")) {
                    DomainResult.Error("כבר יש לך בקשת אימות פעילה")
                } else {
                    DomainResult.Error("מספר טלפון לא תקין")
                }
            } catch (e: Exception) {
                DomainResult.Error("מספר טלפון לא תקין")
            }
        } else if (response.status.value == 400) {
            // 400 carries a typed {detail} string. Branch on the parsed detail.
            val detail = parseApiError(response)?.detail
            if (detail?.contains("already has an active") == true) {
                DomainResult.Error("כבר יש לך בקשת אימות פעילה")
            } else {
                DomainResult.Error(detail ?: "שגיאה בבקשה")
            }
        } else {
            DomainResult.Error("Server error: ${response.status}")
        }
    } catch (e: Exception) {
        DomainResult.Error(e.message ?: "Network error")
    }

    override suspend fun getVerificationStatus(): DomainResult<com.tovtalk.domain.entity.VerificationRequestInfo> = try {
        val body = apiClient.httpClient.get(ApiEndpoints.VERIFICATION_STATUS)
            .body<V2Response<VerificationStatusDto>>()

        body.errors?.takeIf { it.isNotEmpty() }?.let { return DomainResult.Error(it.first().message) }
        val d = body.data ?: return DomainResult.Error("Failed to get verification status")
        DomainResult.Success(
            com.tovtalk.domain.entity.VerificationRequestInfo(
                status = d.status,
                code = d.code,
                phoneToCall = d.phoneToCall,
                canStart = d.canStart,
                retryDaysRemaining = d.retryDaysRemaining
            )
        )
    } catch (e: Exception) {
        DomainResult.Error(e.message ?: "Network error")
    }
    
    /**
     * Parses the structured error body ([ApiErrorBody]) from a non-2xx response.
     * Branching on the typed `detail`/`code` is far more robust than scanning the
     * raw JSON envelope with `bodyAsText().contains(...)`. Returns null if the body
     * can't be parsed as an error object (e.g. Pydantic 422 array).
     */
    private suspend fun parseApiError(response: io.ktor.client.statement.HttpResponse): ApiErrorBody? =
        try {
            val raw = response.bodyAsText()
            raw.takeIf { it.isNotBlank() }
                ?.let { lenientJson.decodeFromString(ApiErrorBody.serializer(), it) }
        } catch (_: Exception) {
            null
        }

    override fun getCachedUser(): Flow<User?> = cachedUserFlow
    
    override suspend fun cacheUser(user: User) {
        cachedUserFlow.value = user
    }
    
    override suspend fun clearCache() {
        cachedUserFlow.value = null
        detailsCache.clear()
        usernameToId.clear()
        followersCursors.clear()
    }
}
